<script setup lang="ts">
import { gsap } from 'gsap'

const { t } = useI18n()
const localePath = useLocalePath()

const props = defineProps<{ searchQuery?: string }>()

const openFaqIndex = ref<number | null>(1)

const toggleFaq = (index: number) => {
  openFaqIndex.value = openFaqIndex.value === index ? null : index
}

// Smooth height + fade transitions for the accordion panels. CSS can't animate
// to `height: auto`, so GSAP measures scrollHeight at enter time and tweens
// height/opacity, then releases the explicit height after the tween so the
// panel re-flows naturally if content changes.
function onEnter(el: Element, done: () => void) {
  const target = el as HTMLElement
  const height = target.scrollHeight
  target.style.overflow = 'hidden'
  gsap.fromTo(
    target,
    { height: 0, opacity: 0 },
    {
      height,
      opacity: 1,
      duration: 0.35,
      ease: 'power2.out',
      onComplete: () => {
        target.style.height = ''
        target.style.overflow = ''
        done()
      },
    },
  )
}

function onLeave(el: Element, done: () => void) {
  const target = el as HTMLElement
  target.style.overflow = 'hidden'
  gsap.fromTo(
    target,
    { height: target.scrollHeight, opacity: 1 },
    {
      height: 0,
      opacity: 0,
      duration: 0.3,
      ease: 'power2.in',
      onComplete: done,
    },
  )
}

const allFaqs = computed(() => [
  { question: t('faq.q2_question'), answer: t('faq.q2_answer') },
  { question: t('faq.q3_question'), answer: t('faq.q3_answer') },
  { question: t('faq.q4_question'), answer: t('faq.q4_answer') },
])

const faqs = computed(() => {
  const q = props.searchQuery?.trim().toLowerCase()
  if (!q) return allFaqs.value
  return allFaqs.value.filter(
    f => f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q),
  )
})

const section = ref<HTMLElement | null>(null)
const dirX = useDirX()

onMounted(() => {
  const el = section.value
  if (!el) return

  const dir = dirX.value

  // Badge pop with spin. Land on the resting tilt (matches the `ltr:-rotate-45 /
  // rtl:rotate-45` CSS, i.e. -45 * dir) rather than 0 — GSAP writes the rotation
  // inline, which would otherwise override the CSS tilt and flatten the badge.
  const badgeRotation = -6 * dir
  gsap.fromTo(el.querySelector('.faq-badge'),
    { opacity: 0, scale: 0.4, rotation: badgeRotation - 25 },
    {
      opacity: 1, scale: 1, rotation: badgeRotation, duration: 0.8, ease: 'back.out(2.2)',
      scrollTrigger: { trigger: el, start: 'top 82%', once: true },
    },
  )

  // Left heading slide from start
  gsap.fromTo(el.querySelector('.faq-heading'),
    { opacity: 0, x: -60 * dir },
    {
      opacity: 1, x: 0, duration: 0.9, ease: 'power3.out',
      scrollTrigger: { trigger: el, start: 'top 82%', once: true },
    },
  )

  // FAQ items stagger up from logical end
  gsap.fromTo(el.querySelectorAll('.faq-item'),
    { opacity: 0, x: 50 * dir, y: 20 },
    {
      opacity: 1, x: 0, y: 0, duration: 0.7, ease: 'power3.out', stagger: 0.12,
      scrollTrigger: { trigger: el.querySelector('.faq-list'), start: 'top 86%', once: true },
    },
  )

  // CTA
  gsap.fromTo(el.querySelector('.faq-cta'),
    { opacity: 0, y: 24, scale: 0.9 },
    {
      opacity: 1, y: 0, scale: 1, duration: 0.6, ease: 'back.out(1.4)',
      scrollTrigger: { trigger: el.querySelector('.faq-cta'), start: 'top 93%', once: true },
    },
  )
})
</script>

<template>
  <section id="faq" ref="section" class="min-h-screen flex flex-col justify-center py-12 md:py-16 lg:py-20 bg-white relative overflow-hidden">
    <!-- Dot grid background -->
    <div class="absolute inset-0 bg-[radial-gradient(circle,_#d1d5db_1.5px,_transparent_1.5px)] [background-size:28px_28px] opacity-50 pointer-events-none" aria-hidden="true"/>

    <div class="container relative grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-center">

      <!-- Left: Heading -->
      <div class="faq-heading">
        <div class="faq-badge inline-block bg-creamy-yellow rounded border-2 border-gray-900 px-4 py-1.5 text-sm font-bold italic shadow-[4px_4px_0_#111827] -translate-y-3 md:translate-x-4 ltr:-rotate-6 rtl:rotate-6">
          {{ t('faq.badge') }}
        </div>
        <h2 class="text-[clamp(2rem,4vw,3.5rem)] font-bold text-gray-900 mb-4 leading-[1.1]">
          {{ t('faq.heading_line1') }}<br>{{ t('faq.heading_line2') }}
        </h2>
        <p class="text-lg text-overcast-night">{{ t('faq.subtitle') }}</p>
      </div>

      <!-- Right: Accordion + CTA -->
      <div>
        <div class="faq-list flex flex-col gap-5 mb-8">
          <p v-if="faqs.length === 0" class="text-base text-gray-500 py-4">
            {{ t('page.faq.no_results') }}
          </p>
          <div
            v-for="(faq, index) in faqs"
            :key="index"
            class="faq-item bg-white border-2 border-gray-900 rounded shadow-[6px_6px_0_#111827] overflow-hidden">
            <button
              class="flex items-center justify-between gap-4 md:gap-6 px-5 md:px-7 py-5.5 text-start text-base md:text-[1.35rem] font-bold text-gray-900 cursor-pointer bg-transparent border-none w-full transition-colors"
              :aria-expanded="openFaqIndex === index"
              @click="toggleFaq(index)">
              <span>{{ faq.question }}</span>
              <!-- +/− toggle: container does a half-spin while the second bar
                   rotates 90° in sync, so "+" smoothly resolves to "−" without
                   any popping or scaling. -->
              <span
                class="size-9 min-w-9 shrink-0 flex items-center justify-center relative transition-transform duration-[450ms] ease-[cubic-bezier(0.65,0,0.35,1)]"
                :class="openFaqIndex === index ? 'rotate-180' : 'rotate-0'">
                <span class="absolute w-[18px] h-[2.5px] bg-gray-900 rounded-full"/>
                <span
                  class="absolute w-[18px] h-[2.5px] bg-gray-900 rounded-full origin-center transition-transform duration-[450ms] ease-[cubic-bezier(0.65,0,0.35,1)]"
                  :class="openFaqIndex === index ? 'rotate-0' : 'rotate-90'"/>
              </span>
            </button>
            <Transition :css="false" @enter="onEnter" @leave="onLeave">
              <div v-if="openFaqIndex === index" class="px-5 md:px-7 pb-5.5 text-sm md:text-base font-medium text-gray-900 leading-[1.75]">
                <p>{{ faq.answer }}</p>
              </div>
            </Transition>
          </div>
        </div>

        <div class="faq-cta text-center">
          <NuxtLink v-magnetic :to="localePath('/faqs')" class="bfm-btn bfm-btn-md bg-cheese-yellow">
            {{ t('faq.cta') }}
          </NuxtLink>
        </div>
      </div>

    </div>
  </section>
</template>
